import React, { useCallback } from 'react';
import { Box, Paper, Typography, TextField, Grid, MenuItem, FormControl, Select } from '@mui/material';
import { useUnifiedState } from '../../context/UnifiedStateContext';
import { rtsPwfucLookupData } from '../../utils/dataManager';

// 코일 타입 정보 인터페이스
interface CoilTypes {
  coolingCoilType: string;
  reheatingCoilType: string;
  preheatingCoilType: string;
  heatingCoilType: string;
}

// VBA 원본 폼 이미지에 따른 정확한 데이터 구조
interface FanSelectionData {
  // 선정 기준 섹션
  criteria: {
    saFanPosition: string; // SA Fan 드롭다운 (Draw(I), Draw(O), Blow(I), Blow(O))
    saFanRatio: number; // SA Fan 숫자 입력
    raFanPosition: string; // RA Fan 드롭다운 (Inside, Outside, None)
    raFanRatio: number; // RA Fan 숫자 입력
    safetyMode: string; // 안전율 모드 (Ext./Int.)
    rfEquipment: string; // RF 장비번호
    safetyPercentage1: number; // % 첫 번째 값
    safetyPercentage2: number; // % 두 번째 값
  };
  
  // Supply Fan 섹션
  supplyFan: {
    efficiency: number; // 효율/S.F
    safetyFactor: number; // 안전율
    type: string; // Type (Air Foil, Backward Curved, Forward Curved)
    specification: string; // 규격 (DS # 5)
    // 계산 결과 (읽기 전용)
    airflow: number; // 풍량(㎥/h)
    staticPressure: number; // 정압(Pa)
    power: number; // 동력(kW)
  };
  
  // Return Fan 섹션
  returnFan: {
    efficiency: number; // 효율/S.F
    safetyFactor: number; // 안전율
    type: string; // Type (Sirocco, Airfoil, Backward)
    specification: string; // 규격 (DS # 4)
    // 계산 결과 (읽기 전용)
    airflow: number; // 풍량(㎥/h)
    staticPressure: number; // 정압(Pa)
    power: number; // 동력(kW)
  };
  
  // Internal S.P, Pa 섹션
  internalPressureDrop: {
    coolingCoil: number;
    reheatingCoil: number;
    preheatingCoil: number;
    heatingCoil: number;
    heatRecovery: number;
    preFilter: number;
    mediumFilter: number;
    finalFilter: number;
    plenumCasing: number;
    chamber: number;
    volumeDamper: number;
    spare1: number;
    spare2: number;
    spare3: number;
    // 계산된 값들
    total: number; // 합계
    safetyFactorPercent: number; // Safety Factor %
    safetyFactorValue: number; // Safety Factor 값
    subTotal: number; // Sub-Total
  };
  
  // SF S.P 합계 섹션
  sfStaticPressure: {
    internal: number; // Internal (Supply Duct 계산값)
    external: number; // External (Return Duct 계산값)
    total: number; // Total (전체 합계)
  };

  // External S.P, Pa 섹션
  externalPressureDrop: {
    supply: {
      ductFriction1: number; // Pa/m 첫 번째
      ductFriction2: number; // Pa/m 두 번째
      ductLength1: number; // m 첫 번째
      ductLength2: number; // m 두 번째
      saValue1: number; // SA 첫 번째
      saValue2: number; // SA 두 번째
    };
    return: {
      ductFriction1: number; // Pa/m 첫 번째
      ductFriction2: number; // Pa/m 두 번째
      ductLength1: number; // m 첫 번째
      ductLength2: number; // m 두 번째
      raValue1: number; // RA 첫 번째
      raValue2: number; // RA 두 번째
    };
    ductFittings: {
      saValue: number;
      raValue: number;
      lossRate: number; // Duct 손실의 적용율(%)
    };
    equipment: {
      terminalUnit: { sa: number; ra: number };
      preReHeatCoil: { sa: number; ra: number };
      oaEaLouver: { sa: number; ra: number };
      diffuserGrille: { sa: number; ra: number };
      flexibleDuct: { sa: number; ra: number };
      volumeDamper: { sa: number; ra: number };
      fireDamper: { sa: number; ra: number };
      soundAttenuator: { sa: number; ra: number };
      raPlenum: { sa: number; ra: number };
      velocityPressure: { sa: number; ra: number };
      spare1: { sa: number; ra: number };
      spare2: { sa: number; ra: number };
      spare3: { sa: number; ra: number };
    };
    // 계산된 값들
    sa: {
      total: number; // SA 합계
      safetyFactorPercent: number; // Safety Factor %
      safetyFactorValue: number; // Safety Factor 값
      subTotal: number; // Sub-Total
    };
    ra: {
      total: number; // RA 합계
      safetyFactorPercent: number; // Safety Factor %
      safetyFactorValue: number; // Safety Factor 값
      subTotal: number; // Sub-Total
    };
  };
}

// VBA 원본 이미지에 따른 기본값 생성 함수
const createDefaultValues = (supplyAirflow: number, returnAirflow: number): FanSelectionData => ({
  criteria: {
    saFanPosition: 'Draw(I)',
    saFanRatio: 1,
    raFanPosition: 'Inside',
    raFanRatio: 1,
    safetyMode: 'Ext.',
    rfEquipment: '',
    safetyPercentage1: 10,
    safetyPercentage2: 10,
  },
  
  supplyFan: {
    efficiency: 65,
    safetyFactor: 15,
    type: 'Air Foil',
    specification: 'DS # 5',
    airflow: supplyAirflow, // AHU 계산 결과의 급기량 사용
    staticPressure: 924,
    power: 15,
  },
  
  returnFan: {
    efficiency: 50,
    safetyFactor: 15,
    type: 'Sirocco',
    specification: 'DS # 4',
    airflow: returnAirflow, // AHU 계산 결과의 환기량 사용
    staticPressure: 337,
    power: 5.5,
  },
  
  internalPressureDrop: {
    coolingCoil: 120,
    reheatingCoil: 0,
    preheatingCoil: 0,
    heatingCoil: 40,
    heatRecovery: 0,
    preFilter: 80,
    mediumFilter: 150,
    finalFilter: 0,
    plenumCasing: 50,
    chamber: 30,
    volumeDamper: 20,
    spare1: 0,
    spare2: 0,
    spare3: 0,
    // 계산된 값들 (초기값)
    total: 0,
    safetyFactorPercent: 10,
    safetyFactorValue: 0,
    subTotal: 0,
  },

  sfStaticPressure: {
    internal: 0,
    external: 0,
    total: 0,
  },
  
  externalPressureDrop: {
    supply: {
      ductFriction1: 1.5,
      ductFriction2: 1.0,
      ductLength1: 0,
      ductLength2: 80,
      saValue1: 0,
      saValue2: 80,
    },
    return: {
      ductFriction1: 1.0,
      ductFriction2: 0.8,
      ductLength1: 80,
      ductLength2: 80,
      raValue1: 80,
      raValue2: 64,
    },
    ductFittings: {
      saValue: 40,
      raValue: 32,
      lossRate: 50,
    },
    equipment: {
      terminalUnit: { sa: 0, ra: 0 },
      preReHeatCoil: { sa: 0, ra: 0 },
      oaEaLouver: { sa: 50, ra: 50 },
      diffuserGrille: { sa: 30, ra: 30 },
      flexibleDuct: { sa: 10, ra: 10 },
      volumeDamper: { sa: 20, ra: 20 },
      fireDamper: { sa: 20, ra: 20 },
      soundAttenuator: { sa: 100, ra: 80 },
      raPlenum: { sa: 0, ra: 0 },
      velocityPressure: { sa: 0, ra: 0 },
      spare1: { sa: 0, ra: 0 },
      spare2: { sa: 0, ra: 0 },
      spare3: { sa: 0, ra: 0 },
    },
    // 계산된 값들 (초기값)
    sa: {
      total: 0,
      safetyFactorPercent: 10,
      safetyFactorValue: 0,
      subTotal: 0,
    },
    ra: {
      total: 0,
      safetyFactorPercent: 10,
      safetyFactorValue: 0,
      subTotal: 0,
    },
  }
});

// 깊은 병합 함수
const deepMerge = (target: any, source: any): any => {
  const result = { ...target };
  for (const key in source) {
    if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
};

// 선정 기준 섹션 - 컴포넌트 외부 정의로 포커스 유지
const FanCriteriaSection = React.memo<{ data: FanSelectionData['criteria']; onUpdate: (field: keyof FanSelectionData['criteria'], value: any) => void; }>(({ data, onUpdate }) => (
  <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #ccc' }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, bgcolor: '#f0f0f0', p: 1 }}>선정 기준</Typography>
    
    {/* 가로 스크롤 가능한 컨테이너 */}
    <Box sx={{ 
      overflowX: 'auto',
      overflowY: 'visible',
      width: '100%',
      pb: 1 // 스크롤바를 위한 여백
    }}>
      {/* 첫 번째 행: SA Fan과 RA Fan */}
    <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center', minWidth: '480px' }}>
      
      {/* SA Fan */}
      <Grid item xs={3}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'blue' }}>SA Fan</Typography>
        <FormControl size="small" fullWidth>
          <Select 
            value={data.saFanPosition}
            onChange={(e) => onUpdate('saFanPosition', e.target.value)}
            size="small" 
            fullWidth
            sx={{ '& .MuiSelect-select': { color: 'blue' } }}
          >
            <MenuItem value="Draw(I)">Draw(I)</MenuItem>
            <MenuItem value="Draw(O)">Draw(O)</MenuItem>
            <MenuItem value="Blow(I)">Blow(I)</MenuItem>
            <MenuItem value="Blow(O)">Blow(O)</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={1.5}>
        <TextField 
          value={data.saFanRatio}
          onChange={(e) => onUpdate('saFanRatio', Number(e.target.value) || 1)}
          size="small" 
          fullWidth
          type="number"
          sx={{ 
            mt: 2.5, 
            bgcolor: 'white', 
            minWidth: '60px', // 선정 기준 섹션 다이나믹 폭
            '& .MuiInputBase-input': { 
              color: 'blue',
              minWidth: '40px', // 입력 필드 최적화
              textAlign: 'center'
            } 
          }}
        />
      </Grid>
      
      {/* RA Fan */}
      <Grid item xs={3}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'red' }}>RA Fan</Typography>
        <FormControl size="small" fullWidth>
          <Select 
            value={data.raFanPosition}
            onChange={(e) => onUpdate('raFanPosition', e.target.value)}
            size="small"
            fullWidth
            sx={{ '& .MuiSelect-select': { color: 'red' } }}
          >
            <MenuItem value="Inside">Inside</MenuItem>
            <MenuItem value="Outside">Outside</MenuItem>
            <MenuItem value="None">None</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={1.5}>
        <TextField
          value={data.raFanPosition === 'None' ? '' : data.raFanRatio}
          onChange={(e) => onUpdate('raFanRatio', Number(e.target.value) || 1)}
          size="small"
          fullWidth
          type="number"
          disabled={data.raFanPosition === 'None'}
          sx={{
            mt: 2.5,
            bgcolor: data.raFanPosition === 'None' ? '#f5f5f5' : 'white',
            minWidth: '60px', // 선정 기준 섹션 다이나믹 폭
            '& .MuiInputBase-input': {
              color: 'red',
              minWidth: '40px', // 입력 필드 최적화
              textAlign: 'center'
            }
          }}
        />
      </Grid>
    </Grid>
    
    {/* 두 번째 행: 안전율 및 RF 장비번호 */}
    <Grid container spacing={1} sx={{ alignItems: 'center', minWidth: '480px' }}>
      <Grid item xs={3}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>안전율(%)</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>내부</Typography>
        <TextField
          value={data.safetyPercentage2}
          onChange={(e) => onUpdate('safetyPercentage2', Number(e.target.value) || 10)}
          size="small"
          fullWidth
          type="number"
          sx={{
            bgcolor: 'white',
            minWidth: '60px', // 선정 기준 다이누믹 폭
            '& .MuiInputBase-input': {
              minWidth: '40px', // 입력 필드 최적화
              textAlign: 'center'
            }
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>외부</Typography>
        <TextField
          value={data.safetyPercentage1}
          onChange={(e) => onUpdate('safetyPercentage1', Number(e.target.value) || 10)}
          size="small"
          fullWidth
          type="number"
          sx={{
            bgcolor: 'white',
            minWidth: '60px', // 선정 기준 다이누믹 폭
            '& .MuiInputBase-input': {
              minWidth: '40px', // 입력 필드 최적화
              textAlign: 'center'
            }
          }}
        />
      </Grid>
      
     
      
      {/* RF 장비번호 */}
      <Grid item xs={5}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>RF 장비번호</Typography>
        <TextField 
          value={data.rfEquipment}
          onChange={(e) => onUpdate('rfEquipment', e.target.value)}
          size="small" 
          fullWidth
          sx={{ 
            bgcolor: 'white',
            minWidth: '120px', // RF 장비번호 필드 충분한 폭
            '& .MuiInputBase-input': {
              minWidth: '100px', // 입력 필드 충분한 폭
              textAlign: 'center'
            }
          }}
        />
      </Grid>
    </Grid>
    </Box>
  </Paper>
));


// Supply Fan 섹션 - 컴포넌트 외부 정의로 포커스 유지
const SupplyFanSection = React.memo<{ data: FanSelectionData['supplyFan']; onUpdate: (field: keyof FanSelectionData['supplyFan'], value: any) => void; }>(({ data, onUpdate }) => {
  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #ccc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, bgcolor: '#f0f0f0', p: 1 }}>Supply Fan</Typography>
      
      {/* 가로 스크롤 가능한 컨테이너 */}
      <Box sx={{ 
        overflowX: 'auto',
        overflowY: 'visible',
        width: '100%',
        pb: 1 // 스크롤바를 위한 여백
      }}>
        {/* 헤더 */}
        <Grid container spacing={1} sx={{ mb: 1, minWidth: '420px' }}>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>효율</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>안전율</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>형식</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>규격</Typography>
        </Grid>
      </Grid>
      
      {/* 값 입력 행 */}
      <Grid container spacing={1} sx={{ mb: 2, minWidth: '420px' }}>
        <Grid item xs={2}>
          <TextField 
            value={data.efficiency}
            onChange={(e) => onUpdate('efficiency', Number(e.target.value) || 65)}
            size="small" 
            fullWidth
            type="number"
            sx={{ 
              bgcolor: '#dff9fb', 
              minWidth: '80px', // Supply Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'blue',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField 
            value={data.safetyFactor}
            onChange={(e) => onUpdate('safetyFactor', Number(e.target.value) || 15)}
            size="small" 
            fullWidth
            type="number"
            sx={{ 
              bgcolor: '#dff9fb', 
              minWidth: '80px', // Supply Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'blue',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField 
            value={data.type}
            onChange={(e) => onUpdate('type', e.target.value)}
            size="small" 
            fullWidth
            sx={{ 
              bgcolor: '#dff9fb', 
              minWidth: '80px', // Supply Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'blue',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField 
            value={data.specification}
            onChange={(e) => onUpdate('specification', e.target.value)}
            size="small" 
            fullWidth
            sx={{ 
              bgcolor: '#dff9fb', 
              minWidth: '80px', // Supply Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'blue',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
      </Grid>
      
        {/* 계산 결과 헤더 */}
        <Grid container spacing={1} sx={{ mb: 1, minWidth: '420px' }}>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>풍량(㎥/h)</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>정압(Pa)</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>동력(kW)</Typography>
        </Grid>
      </Grid>
      
        {/* 계산 결과 값 */}
        <Grid container spacing={1} sx={{ minWidth: '420px' }}>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ textAlign: 'center', border: '1px solid #ccc', p: 0.5, bgcolor: '#dff9fb', color: 'blue' }}>
            {data.airflow.toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ textAlign: 'center', border: '1px solid #ccc', p: 0.5, bgcolor: '#dff9fb', color: 'blue' }}>
            {data.staticPressure}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ textAlign: 'center', border: '1px solid #ccc', p: 0.5, bgcolor: '#dff9fb', color: 'blue' }}>
            {data.power}
          </Typography>
        </Grid>
      </Grid>
      </Box>
    </Paper>
  );
});

// Return Fan 섹션 - 컴포넌트 외부 정의로 포커스 유지
const ReturnFanSection = React.memo<{ data: FanSelectionData['returnFan']; onUpdate: (field: keyof FanSelectionData['returnFan'], value: any) => void; }>(({ data, onUpdate }) => (
    <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #ccc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, bgcolor: '#f0f0f0', p: 1 }}>Return Fan</Typography>
      
      {/* 가로 스크롤 가능한 컨테이너 */}
      <Box sx={{ 
        overflowX: 'auto',
        overflowY: 'visible',
        width: '100%',
        pb: 1 // 스크롤바를 위한 여백
      }}>
        {/* 헤더 */}
        <Grid container spacing={1} sx={{ mb: 1, minWidth: '420px' }}>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>효율</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>안전율</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>형식</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>규격</Typography>
        </Grid>
      </Grid>
      
        {/* 값 입력 행 */}
        <Grid container spacing={1} sx={{ mb: 2, minWidth: '420px' }}>
        <Grid item xs={2}>
          <TextField 
            value={data.efficiency}
            onChange={(e) => onUpdate('efficiency', Number(e.target.value) || 50)}
            size="small" 
            fullWidth
            type="number"
            sx={{ 
              bgcolor: '#ffeaa7', 
              minWidth: '80px', // Return Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'red',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField 
            value={data.safetyFactor}
            onChange={(e) => onUpdate('safetyFactor', Number(e.target.value) || 15)}
            size="small" 
            fullWidth
            type="number"
            sx={{ 
              bgcolor: '#ffeaa7', 
              minWidth: '80px', // Return Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'red',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField 
            value={data.type}
            onChange={(e) => onUpdate('type', e.target.value)}
            size="small" 
            fullWidth
            sx={{ 
              bgcolor: '#ffeaa7', 
              minWidth: '80px', // Return Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'red',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField 
            value={data.specification}
            onChange={(e) => onUpdate('specification', e.target.value)}
            size="small" 
            fullWidth
            sx={{ 
              bgcolor: '#ffeaa7', 
              minWidth: '80px', // Return Fan 섹션 폭 90%로 최적화
              '& .MuiInputBase-input': { 
                color: 'red',
                minWidth: '60px', // 입력 필드 90%로 최적화
                textAlign: 'center'
              } 
            }}
          />
        </Grid>
      </Grid>
      
        {/* 계산 결과 헤더 */}
        <Grid container spacing={1} sx={{ mb: 1, minWidth: '420px' }}>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>풍량(㎥/h)</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>정압(Pa)</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', p: 0.5 }}>동력(kW)</Typography>
        </Grid>
      </Grid>
      
        {/* 계산 결과 값 */}
        <Grid container spacing={1} sx={{ minWidth: '420px' }}>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ textAlign: 'center', border: '1px solid #ccc', p: 0.5, bgcolor: '#ffeaa7', color: 'red' }}>
            {data.airflow.toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ textAlign: 'center', border: '1px solid #ccc', p: 0.5, bgcolor: '#ffeaa7', color: 'red' }}>
            {data.staticPressure}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" sx={{ textAlign: 'center', border: '1px solid #ccc', p: 0.5, bgcolor: '#ffeaa7', color: 'red' }}>
            {data.power}
          </Typography>
        </Grid>
      </Grid>
      </Box>
    </Paper>
  ));

// SF S.P 합계 섹션 - 컴포넌트 외부 정의로 포커스 유지
const SfStaticPressureSection = React.memo<{ data: FanSelectionData['sfStaticPressure']; }>(({ data }) => (
    <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #ccc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, bgcolor: '#f0f0f0', p: 1 }}>
        SF S.P 합계
      </Typography>
      
      <Box sx={{ border: '2px solid #333', p: 1 }}>
        <Grid container spacing={1}>
          {/* Internal 행 */}
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', p: 0.5, border: '1px solid #ccc' }}>
              Internal
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', p: 0.5, border: '1px solid #ccc' }}>
              External
            </Typography>
          </Grid>
          
          {/* 계산 결과 표시 (읽기 전용) */}
          <Grid item xs={6}>
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                border: '1px solid #ccc', 
                p: 0.5, 
                bgcolor: '#e8f4fd',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {data.internal}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                border: '1px solid #ccc', 
                p: 0.5, 
                bgcolor: '#e8f4fd',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {data.external}
            </Typography>
          </Grid>
          
          {/* 합계 행 */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', p: 0.5, border: '2px solid #333', bgcolor: '#fff2cc' }}>
              Total: {data.total} Pa
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  ));

// Internal TextField 컴포넌트 - 포커스 유지를 위한 별도 컴포넌트
const InternalTextField = React.memo<{ 
  value: number; 
  onChange: (value: number) => void; 
  isHidden?: boolean; 
}>(({ value, onChange, isHidden = false }) => {
  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value) || 0);
  }, [onChange]);

  if (isHidden) {
    return (
      <Box sx={{ mb: 1, height: '40px', display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          -
        </Typography>
      </Box>
    );
  }

  return (
    <TextField 
      value={value} 
      onChange={handleChange}
      type="number"
      size="small" 
      fullWidth 
      sx={{ 
        mb: 1, 
        bgcolor: '#e8f4fd',
        minWidth: '38px', // Internal S.P 폭 80%로 축소
        '& .MuiInputBase-input': {
          minWidth: '26px', // 실제 입력 필드 80%로 축소
          textAlign: 'center'
        }
      }} 
    />
  );
});

// Internal Items 상수 - 컴포넌트 외부로 이동하여 재렌더링 방지
const INTERNAL_ITEMS = [
  { name: '냉각 코일', key: 'coolingCoil' as const },
  { name: '재열 코일', key: 'reheatingCoil' as const },
  { name: '예열 코일', key: 'preheatingCoil' as const },
  { name: '가열 코일', key: 'heatingCoil' as const },
  { name: '배열회수기', key: 'heatRecovery' as const },
  { name: 'Pre Filter', key: 'preFilter' as const },
  { name: 'Medium Filter', key: 'mediumFilter' as const },
  { name: 'Final Filter', key: 'finalFilter' as const },
  { name: 'Plenum/Casing', key: 'plenumCasing' as const },
  { name: 'Chamber', key: 'chamber' as const },
  { name: 'Volume Damper', key: 'volumeDamper' as const },
  { name: '', key: 'spare1' as const, isSpare: true },
  { name: '', key: 'spare2' as const, isSpare: true },
  { name: '', key: 'spare3' as const, isSpare: true },
] as const;

// Internal Pressure 섹션 컴포넌트 Props 타입
interface InternalPressureSectionProps {
  data: FanSelectionData['internalPressureDrop'];
  onUpdate: (field: keyof FanSelectionData['internalPressureDrop'], value: number) => void;
  coilTypes?: CoilTypes | null;
  heatRecoveryType?: string;
}

// Internal Pressure 섹션 - 포커스 문제 해결
const InternalPressureSection = React.memo<InternalPressureSectionProps>(({ data, onUpdate, coilTypes, heatRecoveryType }) => {
  
  // 코일 타입 및 배열회수기 타입에 따라 필드가 숨겨져야 하는지 판단
  const isCoilFieldHidden = (coilKey: string) => {
    if (!coilTypes) return false;
    
    switch (coilKey) {
      case 'coolingCoil':
        return coilTypes.coolingCoilType === 'None';
      case 'reheatingCoil':
        return coilTypes.reheatingCoilType === 'None';
      case 'preheatingCoil':
        return coilTypes.preheatingCoilType === 'None';
      case 'heatingCoil':
        return coilTypes.heatingCoilType === 'None';
      case 'heatRecovery':
        return heatRecoveryType === 'None';
      default:
        return false;
    }
  };

  return (
  <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #ccc', overflow: 'hidden' }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, bgcolor: '#f0f0f0', p: 1 }}>
      내부(S.P, Pa)
    </Typography>
    
    {/* 가로 스크롤 가능한 컨테이너 */}
    <Box sx={{ 
      overflowX: 'auto',
      minWidth: '100%',
      pb: 1 // 스크롤바를 위한 여백
    }}>
      {/* 헤더 아래 구분선 */}
      <Box sx={{ 
        width: '100%', 
        height: '1px', 
        bgcolor: '#ccc', 
        mb: 2,
        mx: 0,
        minWidth: '200px' // Internal S.P 폭 더 축소
      }} />
      
      <Grid container spacing={1} sx={{ minWidth: '200px' }}> {/* Internal S.P 폭 더 축소 */}
        {INTERNAL_ITEMS.map((item) => (
          <React.Fragment key={`internal-${item.key}`}> 
            <Grid item xs={8}>
              {item.isSpare ? (
                <TextField
                  placeholder="항목 이름을 입력하세요"
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    fontSize: '0.75rem', // 더 작은 폰트 크기
                    color: isCoilFieldHidden(item.key) ? 'text.disabled' : 'inherit',
                    fontStyle: isCoilFieldHidden(item.key) ? 'italic' : 'normal'
                  }}
                >
                  {item.name} {isCoilFieldHidden(item.key) && '(비활성)'}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4}>
              <InternalTextField 
                value={data[item.key]} 
                onChange={(value) => onUpdate(item.key, value)}
                isHidden={isCoilFieldHidden(item.key)}
              />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </Box>
    
    {/* 구분선 */}
    <Box sx={{ 
      width: '100%', 
      height: '1px', 
      bgcolor: '#ccc', 
      mt: 2,
      mx: 0
    }} />
  </Paper>
  );
});

// External Pressure 섹션 - 컴포넌트 외부 정의로 포커스 유지
interface ExternalPressureSectionProps {
  data: FanSelectionData['externalPressureDrop'];
  onUpdateSupply: (field: keyof FanSelectionData['externalPressureDrop']['supply'], value: number) => void;
  onUpdateReturn: (field: keyof FanSelectionData['externalPressureDrop']['return'], value: number) => void;
  onUpdateDuctFittings: (field: keyof FanSelectionData['externalPressureDrop']['ductFittings'], value: number) => void;
  onUpdateEquipment: (equipmentKey: keyof FanSelectionData['externalPressureDrop']['equipment'], field: 'sa' | 'ra', value: number) => void;
  raFanPosition: string;
}

const ExternalPressureSection = React.memo<ExternalPressureSectionProps>(({ data, onUpdateSupply, onUpdateReturn, onUpdateDuctFittings, onUpdateEquipment, raFanPosition }) => {
  const isRADisabled = raFanPosition === 'None';

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #ccc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, bgcolor: '#f0f0f0', p: 1 }}>외부(S.P, Pa)</Typography>
      
      {/* 가로 스크롤 가능한 컨테이너 */}
      <Box sx={{ 
        overflowX: 'auto',
        overflowY: 'visible',
        width: '100%',
        pb: 1 // 스크롤바를 위한 여백
      }}>
        {/* 헤더 */}
        <Grid container spacing={1} sx={{ mb: 1, minWidth: '520px' }}> {/* 최소 폭 더 확장 */}
        <Grid item xs={3}></Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', bgcolor: '#f0f0f0', p: 0.5 }}>Pa/m</Typography>
        </Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', bgcolor: '#f0f0f0', p: 0.5 }}>m</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', bgcolor: '#f0f0f0', p: 0.5 }}>SA</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', border: '1px solid #ccc', bgcolor: '#f0f0f0', p: 0.5 }}>RA</Typography>
        </Grid>
      </Grid>
      
      {/* 헤더 아래 구분선 */}
      <Box sx={{ 
        width: '100%', 
        height: '1px', 
        bgcolor: '#ccc', 
        mb: 2,
        mx: 0
      }} />
      
        {/* SA 첫 번째 행 */}
        <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center', minWidth: '520px' }}>
        <Grid item xs={3}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>SA</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField 
            value={data.supply.ductFriction1}
            onChange={(e) => onUpdateSupply('ductFriction1', Number(e.target.value) || 0)}
            type="number"
            size="small" 
            fullWidth 
            sx={{
              bgcolor: '#e8f4fd',
              minWidth: '60px', // 넓어진 섹션에 맞춰 확장
              '& .MuiInputBase-input': {
                color: 'blue',
                minWidth: '40px', // 넓어진 섹션에 맞춰 확장
                textAlign: 'center'
              }
            }} 
          />
        </Grid>
        <Grid item xs={1}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>×</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField 
            value={data.supply.ductLength1}
            onChange={(e) => onUpdateSupply('ductLength1', Number(e.target.value) || 0)}
            type="number"
            size="small" 
            fullWidth 
            sx={{
              bgcolor: 'white',
              minWidth: '60px', // 넓어진 섹션에 맞춰 확장
              '& .MuiInputBase-input': {
                color: 'blue',
                minWidth: '40px', // 넓어진 섹션에 맞춰 확장
                textAlign: 'center'
              }
            }} 
          />
        </Grid>
        <Grid item xs={2}>
          <TextField 
            value={Math.round((data.supply.ductFriction1 * data.supply.ductLength1) * 10) / 10}
            InputProps={{ readOnly: true }}
            type="number"
            size="small" 
            fullWidth 
            sx={{
              bgcolor: '#f0f9fc',
              minWidth: '80px', // 넓어진 섹션에 맞춰 확장
              '& .MuiInputBase-input': {
                color: 'blue',
                minWidth: '60px', // 넓어진 섹션에 맞춰 확장
                textAlign: 'center'
              }
            }} 
          />
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>-</Typography>
        </Grid>
      </Grid>
      
        {/* SA 두 번째 행 */}
        <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center', minWidth: '520px' }}>
        <Grid item xs={3}></Grid>
        <Grid item xs={2}>
          <TextField 
            value={data.supply.ductFriction2}
            onChange={(e) => onUpdateSupply('ductFriction2', Number(e.target.value) || 0)}
            type="number"
            size="small" 
            fullWidth 
            sx={{
              bgcolor: '#e8f4fd',
              minWidth: '60px', // 넓어진 섹션에 맞춰 확장
              '& .MuiInputBase-input': {
                color: 'blue',
                minWidth: '40px', // 넓어진 섹션에 맞춰 확장
                textAlign: 'center'
              }
            }} 
          />
        </Grid>
        <Grid item xs={1}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>×</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField 
            value={data.supply.ductLength2}
            onChange={(e) => onUpdateSupply('ductLength2', Number(e.target.value) || 0)}
            type="number"
            size="small" 
            fullWidth 
            sx={{
              bgcolor: 'white',
              minWidth: '60px', // 넓어진 섹션에 맞춰 확장
              '& .MuiInputBase-input': {
                color: 'blue',
                minWidth: '40px', // 넓어진 섹션에 맞춰 확장
                textAlign: 'center'
              }
            }} 
          />
        </Grid>
        <Grid item xs={2}>
          <TextField 
            value={Math.round((data.supply.ductFriction2 * data.supply.ductLength2) * 10) / 10}
            InputProps={{ readOnly: true }}
            type="number"
            size="small" 
            fullWidth 
            sx={{
              bgcolor: '#f0f9fc',
              minWidth: '80px', // 넓어진 섹션에 맞춰 확장
              '& .MuiInputBase-input': {
                color: 'blue',
                minWidth: '60px', // 넓어진 섹션에 맞춰 확장
                textAlign: 'center'
              }
            }} 
          />
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>-</Typography>
        </Grid>
      </Grid>
      
        {/* RA 첫 번째 행 */}
        <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center', minWidth: '520px' }}>
        <Grid item xs={3}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>RA</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField
            value={isRADisabled ? '' : data.return.ductFriction1}
            onChange={(e) => onUpdateReturn('ductFriction1', Number(e.target.value) || 0)}
            type="number"
            size="small"
            fullWidth
            disabled={isRADisabled}
            sx={{
              bgcolor: isRADisabled ? '#f5f5f5' : '#e8f4fd',
              minWidth: '60px', // External S.P 적절한 최소 폭 유지
              '& .MuiInputBase-input': {
                color: 'red',
                minWidth: '40px', // 실제 입력 필드 적절한 최소 폭
                textAlign: 'center'
              }
            }}
          />
        </Grid>
        <Grid item xs={1}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>×</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField
            value={isRADisabled ? '' : data.return.ductLength1}
            onChange={(e) => onUpdateReturn('ductLength1', Number(e.target.value) || 0)}
            type="number"
            size="small"
            fullWidth
            disabled={isRADisabled}
            sx={{
              bgcolor: isRADisabled ? '#f5f5f5' : 'white',
              minWidth: '60px', // External S.P 적절한 최소 폭 유지
              '& .MuiInputBase-input': {
                color: 'red',
                minWidth: '40px', // 실제 입력 필드 적절한 최소 폭
                textAlign: 'center'
              }
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>-</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField
            value={isRADisabled ? '' : Math.round((data.return.ductFriction1 * data.return.ductLength1) * 10) / 10}
            InputProps={{ readOnly: true }}
            type="number"
            size="small"
            fullWidth
            sx={{
              bgcolor: isRADisabled ? '#f5f5f5' : '#fffaf0',
              minWidth: '80px', // SA와 동일한 폭
              '& .MuiInputBase-input': {
                color: 'red',
                minWidth: '60px' // SA와 동일한 폭
              }
            }}
          />
        </Grid>
      </Grid>
      
        {/* RA 두 번째 행 */}
        <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center', minWidth: '520px' }}>
        <Grid item xs={3}></Grid>
        <Grid item xs={2}>
          <TextField
            value={isRADisabled ? '' : data.return.ductFriction2}
            onChange={(e) => onUpdateReturn('ductFriction2', Number(e.target.value) || 0)}
            type="number"
            size="small"
            fullWidth
            disabled={isRADisabled}
            sx={{
              bgcolor: isRADisabled ? '#f5f5f5' : '#e8f4fd',
              minWidth: '60px', // External S.P 적절한 최소 폭 유지
              '& .MuiInputBase-input': {
                color: 'red',
                minWidth: '40px', // 실제 입력 필드 적절한 최소 폭
                textAlign: 'center'
              }
            }}
          />
        </Grid>
        <Grid item xs={1}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>×</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField
            value={isRADisabled ? '' : data.return.ductLength2}
            onChange={(e) => onUpdateReturn('ductLength2', Number(e.target.value) || 0)}
            type="number"
            size="small"
            fullWidth
            disabled={isRADisabled}
            sx={{
              bgcolor: isRADisabled ? '#f5f5f5' : 'white',
              minWidth: '60px', // External S.P 적절한 최소 폭 유지
              '& .MuiInputBase-input': {
                color: 'red',
                minWidth: '40px', // 실제 입력 필드 적절한 최소 폭
                textAlign: 'center'
              }
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2" sx={{ textAlign: 'center', p: 0.5 }}>-</Typography>
        </Grid>
        <Grid item xs={2}>
          <TextField
            value={isRADisabled ? '' : Math.round((data.return.ductFriction2 * data.return.ductLength2) * 10) / 10}
            InputProps={{ readOnly: true }}
            type="number"
            size="small"
            fullWidth
            sx={{
              bgcolor: isRADisabled ? '#f5f5f5' : '#fffaf0',
              minWidth: '80px', // SA와 동일한 폭
              '& .MuiInputBase-input': {
                color: 'red',
                minWidth: '60px' // SA와 동일한 폭
              }
            }}
          />
        </Grid>
      </Grid>
      
        {/* Duct Fittings 첫 번째 행 */}
        <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center', minWidth: '520px' }}>
        <Grid item xs={3}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Duct Fittings</Typography>
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={2}>
          <TextField
            value={data.ductFittings.saValue}
            onChange={(e) => onUpdateDuctFittings('saValue', Number(e.target.value) || 0)}
            type="number"
            size="small"
            fullWidth
            sx={{
              bgcolor: '#dff9fb',
              minWidth: '80px', // SA와 동일한 폭
              '& .MuiInputBase-input': {
                color: 'blue',
                minWidth: '60px' // SA와 동일한 폭
              }
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            value={isRADisabled ? '' : data.ductFittings.raValue}
            onChange={(e) => onUpdateDuctFittings('raValue', Number(e.target.value) || 0)}
            type="number"
            size="small"
            fullWidth
            disabled={isRADisabled}
            sx={{
              bgcolor: isRADisabled ? '#f5f5f5' : '#ffeaa7',
              minWidth: '80px', // RA와 동일한 폭
              '& .MuiInputBase-input': {
                color: 'red',
                minWidth: '60px' // RA와 동일한 폭
              }
            }}
          />
        </Grid>
      </Grid>
      
        {/* Duct Fittings 두 번째 행 - Duct 손실의 적용율(%) */}
        <Grid container spacing={1} sx={{ mb: 2, alignItems: 'center', minWidth: '520px' }}>
        <Grid item xs={3}>
          <Typography variant="body2" sx={{ textAlign: 'right', pr: 1 }}>덕트 손실 적용율(%):</Typography>
        </Grid>
        <Grid item xs={3}>
          <TextField
            value={data.ductFittings.lossRate}
            onChange={(e) => onUpdateDuctFittings('lossRate', Number(e.target.value) || 0)}
            type="number"
            size="small"
            fullWidth
            sx={{ bgcolor: '#ffffff', textAlign: 'center' }}
          />
        </Grid>
        <Grid item xs={6}></Grid>
      </Grid>

      {/* 구분선 */}
      <Box sx={{ 
        width: '100%', 
        height: '1px', 
        bgcolor: '#ccc', 
        mb: 2,
        mx: 0
      }} />
      
      {/* 각종 설비 항목들 */}
      <Box sx={{ mt: 2 }}>
        {[ 
          { name: 'Terminal Unit', key: 'terminalUnit' as const, hasSA: true, hasRA: false },
          { name: 'Pre(Re) Heat\'g Coil', key: 'preReHeatCoil' as const, hasSA: true, hasRA: false },
          { name: 'OA/EA Louver', key: 'oaEaLouver' as const, hasSA: true, hasRA: true },
          { name: 'Diffuser/Grille', key: 'diffuserGrille' as const, hasSA: true, hasRA: true },
          { name: 'Flexible Duct', key: 'flexibleDuct' as const, hasSA: true, hasRA: true },
          { name: 'Volume Damper', key: 'volumeDamper' as const, hasSA: true, hasRA: true },
          { name: 'Fire Damper', key: 'fireDamper' as const, hasSA: true, hasRA: true },
          { name: 'Sound Attenuator', key: 'soundAttenuator' as const, hasSA: true, hasRA: true },
          { name: 'RA Plenum', key: 'raPlenum' as const, hasSA: false, hasRA: true },
          { name: 'Velocity Pressure', key: 'velocityPressure' as const, hasSA: false, hasRA: true },
          { name: '', key: 'spare1' as const, hasSA: true, hasRA: true, isSpare: true },
          { name: '', key: 'spare2' as const, hasSA: true, hasRA: true, isSpare: true },
          { name: '', key: 'spare3' as const, hasSA: true, hasRA: true, isSpare: true }
        ].map((item, index) => (
          <Grid container spacing={1} sx={{ mb: 1, minWidth: '520px' }} key={`external-equipment-${item.key}`}>
            <Grid item xs={8}>
              {item.isSpare ? (
                <TextField
                  placeholder="항목 이름을 입력하세요"
                  size="small"
                  fullWidth
                />
              ) : (
                <Typography variant="body2">{item.name}</Typography>
              )}
            </Grid>
            <Grid item xs={2}>
              {item.hasSA ? (
                <TextField 
                  value={data.equipment?.[item.key]?.sa ?? 0}
                  onChange={(e) => onUpdateEquipment(item.key, 'sa', Number(e.target.value) || 0)}
                  size="small" 
                  fullWidth 
                  type="number"
                  sx={{ 
                    bgcolor: '#dff9fb', 
                    minWidth: '80px', // RA와 동일한 폭
                    '& .MuiInputBase-input': {
                      color: 'red',
                      minWidth: '60px' // RA와 동일한 폭
                    }
                  }}
                />
              ) : (
                <Box sx={{ height: '32px' }}></Box>
              )}
            </Grid>
            <Grid item xs={2}>
              {item.hasRA ? (
                <TextField
                  value={isRADisabled ? '' : (data.equipment?.[item.key]?.ra ?? 0)}
                  onChange={(e) => onUpdateEquipment(item.key, 'ra', Number(e.target.value) || 0)}
                  size="small"
                  fullWidth
                  type="number"
                  disabled={isRADisabled}
                  sx={{
                    bgcolor: isRADisabled ? '#f5f5f5' : '#ffeaa7',
                    minWidth: '80px', // RA와 동일한 폭
                    '& .MuiInputBase-input': {
                      color: 'red',
                      minWidth: '60px' // RA와 동일한 폭
                    }
                  }}
                />
              ) : (
                <Box sx={{ height: '32px' }}></Box>
              )}
            </Grid>
          </Grid>
        ))}
      </Box>
    </Box>
  </Paper>
  );
});

interface FanSelectionFormProps {
  calculatedSupplyAirflow?: number;
  calculatedReturnAirflow?: number;
}

const FanSelectionForm: React.FC<FanSelectionFormProps> = ({
  calculatedSupplyAirflow: propSupplyAirflow,
  calculatedReturnAirflow: propReturnAirflow
}) => {

  const { state, dispatch } = useUnifiedState();

  // 현재 AHU 데이터 가져오기
  const currentAHUData = React.useMemo(() => {
    if (!state.currentAHUId) return null;
    return state.ahus.find(ahu => ahu.id === state.currentAHUId);
  }, [state.currentAHUId, state.ahus]);

  // ⚠️ 중요: ahuCalculationResults는 읽기 전용으로만 사용해야 함
  // Props (propSupplyAirflow, propReturnAirflow)가 항상 최신값이므로 우선순위를 가져야 함
  // ahuCalculationResults를 기반으로 fanData를 업데이트하는 useEffect를 추가하면 안됨 (버그 발생)
  const ahuCalculationResults = state.tempData?.ahuCalculationResults;

  // Props 우선, 없으면 ahuCalculationResults 사용 (fallback만 사용)
  const calculatedSupplyAirflow = propSupplyAirflow !== undefined ? propSupplyAirflow : (ahuCalculationResults?.supplyAirflow ?? 25600);
  const calculatedReturnAirflow = propReturnAirflow !== undefined ? propReturnAirflow : (ahuCalculationResults?.returnAirflow ?? 19300);

  // 현재 AHU의 코일 타입 정보 가져오기
  const getCurrentAHUCoilTypes = React.useCallback((): CoilTypes | null => {
    if (!currentAHUData?.formData) return null;
    
    return {
      coolingCoilType: currentAHUData.formData.coolingCoilType || 'Water',
      reheatingCoilType: currentAHUData.formData.reheatingCoilType || 'Water',
      preheatingCoilType: currentAHUData.formData.preheatingCoilType || 'None',
      heatingCoilType: currentAHUData.formData.heatingCoilType || 'None'
    };
  }, [currentAHUData]);

  // 현재 AHU의 배열회수기 타입 정보 가져오기
  const getCurrentAHUHeatRecoveryType = React.useCallback((): string => {
    if (!currentAHUData?.formData) return 'None';
    return currentAHUData.formData.heatRecoveryType || 'None';
  }, [currentAHUData]);

  // 코일 타입 정보
  const [coilTypes, setCoilTypes] = React.useState(getCurrentAHUCoilTypes());

  // 배열회수기 타입 정보
  const [heatRecoveryType, setHeatRecoveryType] = React.useState(getCurrentAHUHeatRecoveryType());

  // AHU 변경 시 코일 타입 및 배열회수기 타입 정보 업데이트
  React.useEffect(() => {
    setCoilTypes(getCurrentAHUCoilTypes());
    setHeatRecoveryType(getCurrentAHUHeatRecoveryType());
  }, [state.currentAHUId, getCurrentAHUCoilTypes, getCurrentAHUHeatRecoveryType]);

  // AHU 폼 데이터 변경을 실시간으로 감지하기 위한 폴링
  React.useEffect(() => {
    const interval = setInterval(() => {
      const newCoilTypes = getCurrentAHUCoilTypes();
      if (newCoilTypes && JSON.stringify(newCoilTypes) !== JSON.stringify(coilTypes)) {
        setCoilTypes(newCoilTypes);
      }
      
      const newHeatRecoveryType = getCurrentAHUHeatRecoveryType();
      if (newHeatRecoveryType !== heatRecoveryType) {
        setHeatRecoveryType(newHeatRecoveryType);
      }
    }, 1000); // 1초마다 체크

    return () => clearInterval(interval);
  }, [coilTypes, heatRecoveryType, getCurrentAHUCoilTypes, getCurrentAHUHeatRecoveryType]);

  // 통일된 단일 상태 관리
  // AHU 계산 결과를 기반으로 한 기본값 생성
  const defaultValues = React.useMemo(() => 
    createDefaultValues(calculatedSupplyAirflow, calculatedReturnAirflow),
    [calculatedSupplyAirflow, calculatedReturnAirflow]
  );

  // fanData 초기값은 defaultValues로 시작 (저장된 데이터는 useEffect에서 로드)
  const [fanData, setFanData] = React.useState<FanSelectionData>(defaultValues);

  // 초기 마운트 시 한 번만 저장된 데이터 로드 (props가 유효하지 않을 때만)
  const hasLoadedInitialDataRef = React.useRef(false);
  React.useEffect(() => {
    // props로 유효한 값이 들어오면 저장된 데이터를 로드하지 않음
    const hasValidProps = calculatedSupplyAirflow > 0 || calculatedReturnAirflow > 0;

    if (!hasLoadedInitialDataRef.current && currentAHUData?.fanSelectionData && !hasValidProps) {
      hasLoadedInitialDataRef.current = true;
      setFanData(currentAHUData.fanSelectionData);
    } else if (hasValidProps) {
      hasLoadedInitialDataRef.current = true; // 플래그 설정하여 나중에 로드 방지
    }
  }, []); // 빈 배열: 마운트 시 한 번만

  // AHU가 변경될 때만 저장된 데이터 로드
  const prevAHUIdRef = React.useRef(state.currentAHUId);
  React.useEffect(() => {
    // AHU ID가 실제로 변경되었을 때만 실행
    if (prevAHUIdRef.current !== state.currentAHUId) {
      console.log(`🔄 [FAN] AHU 변경 감지:`, {
        이전AHU: prevAHUIdRef.current,
        현재AHU: state.currentAHUId
      });

      prevAHUIdRef.current = state.currentAHUId;
      hasLoadedInitialDataRef.current = true; // 플래그 업데이트

      // 새 AHU의 저장된 데이터 로드
      const savedFanData = currentAHUData?.fanSelectionData;
      if (savedFanData) {
        // console.log(`✅ [FAN] fanSelectionData 로드 성공:`, {
        //   ahuId: state.currentAHUId,
        //   reheatingCoil: savedFanData.internalPressureDrop?.reheatingCoil
        // });
        setFanData(savedFanData);
      } else {
        console.warn(`⚠️ [FAN] fanSelectionData 없음 - 기본값 사용:`, {
          ahuId: state.currentAHUId,
          currentAHUData: currentAHUData ? 'exists' : 'null'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentAHUId]); // ⚠️ 중요: currentAHUData를 dependency에 추가하면 안됨 (무한 루프 발생)

  // ✅ 핵심: 계산된 풍량값(props)이 변경될 때 fanData 업데이트
  // 이것이 '풍량 선정' 탭의 계산값을 'FAN 선정' 탭에 반영하는 핵심 로직
  React.useEffect(() => {
    // 값이 실제로 변경되었는지 확인
    setFanData(prevData => {
      // 변경이 없으면 업데이트하지 않음 (불필요한 리렌더링 방지)
      if (
        prevData.supplyFan.airflow === calculatedSupplyAirflow &&
        prevData.returnFan.airflow === calculatedReturnAirflow
      ) {
        return prevData;
      }

      // Props로 전달된 최신 계산값으로 업데이트
      const newData = {
        ...prevData,
        supplyFan: {
          ...prevData.supplyFan,
          airflow: calculatedSupplyAirflow
          // 동력은 별도 useEffect에서 재계산됨
        },
        returnFan: {
          ...prevData.returnFan,
          airflow: calculatedReturnAirflow
          // 동력은 별도 useEffect에서 재계산됨
        }
      };

      return newData;
    });
  }, [calculatedSupplyAirflow, calculatedReturnAirflow]); // Props만 dependency로 사용

  // Internal/External S.P의 Safety Factor와 Sub-Total 자동 계산
  React.useEffect(() => {
    setFanData(prevData => {
      // Internal S.P 합계 계산
      const internalTotal =
        prevData.internalPressureDrop.coolingCoil +
        prevData.internalPressureDrop.reheatingCoil +
        prevData.internalPressureDrop.preheatingCoil +
        prevData.internalPressureDrop.heatingCoil +
        prevData.internalPressureDrop.heatRecovery +
        prevData.internalPressureDrop.preFilter +
        prevData.internalPressureDrop.mediumFilter +
        prevData.internalPressureDrop.finalFilter +
        prevData.internalPressureDrop.plenumCasing +
        prevData.internalPressureDrop.chamber +
        prevData.internalPressureDrop.volumeDamper +
        prevData.internalPressureDrop.spare1 +
        prevData.internalPressureDrop.spare2 +
        prevData.internalPressureDrop.spare3;

      const internalSafetyPercent = prevData.criteria.safetyPercentage2 || 10;
      const internalSafetyValue = Math.round(internalTotal * internalSafetyPercent / 100 * 10) / 10;
      const internalSubTotal = Math.round((internalTotal + internalSafetyValue) * 10) / 10;

      // External S.P SA 합계 계산 (실시간 Duct 계산 포함)
      const saFriction1 = prevData.externalPressureDrop.supply.ductFriction1 || 0;
      const saLength1 = prevData.externalPressureDrop.supply.ductLength1 || 0;
      const saValue1 = (saFriction1 * saLength1 !== 0) ? Math.round(saFriction1 * saLength1 * 10) / 10 : 0;

      const saFriction2 = prevData.externalPressureDrop.supply.ductFriction2 || 0;
      const saLength2 = prevData.externalPressureDrop.supply.ductLength2 || 0;
      const saValue2 = (saFriction2 * saLength2 !== 0) ? Math.round(saFriction2 * saLength2 * 10) / 10 : 0;

      const saTotal =
        saValue1 +
        saValue2 +
        prevData.externalPressureDrop.ductFittings.saValue +
        prevData.externalPressureDrop.equipment.terminalUnit.sa +
        prevData.externalPressureDrop.equipment.preReHeatCoil.sa +
        prevData.externalPressureDrop.equipment.oaEaLouver.sa +
        prevData.externalPressureDrop.equipment.diffuserGrille.sa +
        prevData.externalPressureDrop.equipment.flexibleDuct.sa +
        prevData.externalPressureDrop.equipment.volumeDamper.sa +
        prevData.externalPressureDrop.equipment.fireDamper.sa +
        prevData.externalPressureDrop.equipment.soundAttenuator.sa +
        prevData.externalPressureDrop.equipment.raPlenum.sa +
        prevData.externalPressureDrop.equipment.velocityPressure.sa;

      const externalSafetyPercent = prevData.criteria.safetyPercentage1 || 10;
      const saSafetyValue = Math.round(saTotal * externalSafetyPercent / 100 * 10) / 10;
      const saSubTotal = Math.round((saTotal + saSafetyValue) * 10) / 10;

      // External S.P RA 합계 계산 (실시간 Duct 계산 포함)
      const raFriction1 = prevData.externalPressureDrop.return.ductFriction1 || 0;
      const raLength1 = prevData.externalPressureDrop.return.ductLength1 || 0;
      const raValue1 = (raFriction1 * raLength1 !== 0) ? Math.round(raFriction1 * raLength1 * 10) / 10 : 0;

      const raFriction2 = prevData.externalPressureDrop.return.ductFriction2 || 0;
      const raLength2 = prevData.externalPressureDrop.return.ductLength2 || 0;
      const raValue2 = (raFriction2 * raLength2 !== 0) ? Math.round(raFriction2 * raLength2 * 10) / 10 : 0;

      const raTotal =
        raValue1 +
        raValue2 +
        prevData.externalPressureDrop.ductFittings.raValue +
        prevData.externalPressureDrop.equipment.terminalUnit.ra +
        prevData.externalPressureDrop.equipment.preReHeatCoil.ra +
        prevData.externalPressureDrop.equipment.oaEaLouver.ra +
        prevData.externalPressureDrop.equipment.diffuserGrille.ra +
        prevData.externalPressureDrop.equipment.flexibleDuct.ra +
        prevData.externalPressureDrop.equipment.volumeDamper.ra +
        prevData.externalPressureDrop.equipment.fireDamper.ra +
        prevData.externalPressureDrop.equipment.soundAttenuator.ra +
        prevData.externalPressureDrop.equipment.raPlenum.ra +
        prevData.externalPressureDrop.equipment.velocityPressure.ra;

      const raSafetyValue = Math.round(raTotal * externalSafetyPercent / 100 * 10) / 10;
      const raSubTotal = Math.round((raTotal + raSafetyValue) * 10) / 10;

      // 값이 변경되지 않았으면 리렌더링 방지
      if (
        prevData.internalPressureDrop.total === internalTotal &&
        prevData.internalPressureDrop.safetyFactorPercent === internalSafetyPercent &&
        prevData.internalPressureDrop.safetyFactorValue === internalSafetyValue &&
        prevData.internalPressureDrop.subTotal === internalSubTotal &&
        prevData.externalPressureDrop.sa.total === saTotal &&
        prevData.externalPressureDrop.sa.safetyFactorPercent === externalSafetyPercent &&
        prevData.externalPressureDrop.sa.safetyFactorValue === saSafetyValue &&
        prevData.externalPressureDrop.sa.subTotal === saSubTotal &&
        prevData.externalPressureDrop.ra.total === raTotal &&
        prevData.externalPressureDrop.ra.safetyFactorPercent === externalSafetyPercent &&
        prevData.externalPressureDrop.ra.safetyFactorValue === raSafetyValue &&
        prevData.externalPressureDrop.ra.subTotal === raSubTotal
      ) {
        return prevData;
      }

      return {
        ...prevData,
        internalPressureDrop: {
          ...prevData.internalPressureDrop,
          total: internalTotal,
          safetyFactorPercent: internalSafetyPercent,
          safetyFactorValue: internalSafetyValue,
          subTotal: internalSubTotal,
        },
        externalPressureDrop: {
          ...prevData.externalPressureDrop,
          sa: {
            total: saTotal,
            safetyFactorPercent: externalSafetyPercent,
            safetyFactorValue: saSafetyValue,
            subTotal: saSubTotal,
          },
          ra: {
            total: raTotal,
            safetyFactorPercent: externalSafetyPercent,
            safetyFactorValue: raSafetyValue,
            subTotal: raSubTotal,
          },
        },
      };
    });
  }, [
    fanData.internalPressureDrop.coolingCoil,
    fanData.internalPressureDrop.reheatingCoil,
    fanData.internalPressureDrop.preheatingCoil,
    fanData.internalPressureDrop.heatingCoil,
    fanData.internalPressureDrop.heatRecovery,
    fanData.internalPressureDrop.preFilter,
    fanData.internalPressureDrop.mediumFilter,
    fanData.internalPressureDrop.finalFilter,
    fanData.internalPressureDrop.plenumCasing,
    fanData.internalPressureDrop.chamber,
    fanData.internalPressureDrop.volumeDamper,
    fanData.internalPressureDrop.spare1,
    fanData.internalPressureDrop.spare2,
    fanData.internalPressureDrop.spare3,
    fanData.criteria.safetyPercentage1,
    fanData.criteria.safetyPercentage2,
    fanData.externalPressureDrop.supply.ductFriction1,
    fanData.externalPressureDrop.supply.ductLength1,
    fanData.externalPressureDrop.supply.ductFriction2,
    fanData.externalPressureDrop.supply.ductLength2,
    fanData.externalPressureDrop.return.ductFriction1,
    fanData.externalPressureDrop.return.ductLength1,
    fanData.externalPressureDrop.return.ductFriction2,
    fanData.externalPressureDrop.return.ductLength2,
    fanData.externalPressureDrop.ductFittings.saValue,
    fanData.externalPressureDrop.ductFittings.raValue,
    fanData.externalPressureDrop.equipment,
  ]);

  // 디바운스를 위한 ref
  const saveTimeoutRef = React.useRef<NodeJS.Timeout>();

  // fanData가 변경될 때 디바운스를 적용하여 UnifiedState에 저장
  React.useEffect(() => {
    if (!state.currentAHUId) return;

    // 이전 타이머 취소
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // 300ms 디바운스 적용하여 빈번한 저장 방지
    saveTimeoutRef.current = setTimeout(() => {
      dispatch({
        type: 'UPDATE_FAN_SELECTION_DATA',
        payload: {
          ahuId: state.currentAHUId,
          fanData: fanData
        }
      });
    }, 300);

    // 클린업: 컴포넌트 언마운트 시 타이머 취소
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [fanData, state.currentAHUId, dispatch]);

  // ========================================================================================
  // Supply Fan 동력 계산 함수 - VBA basiscode.bas pwfuc 함수와 100% 동일한 로직
  // ========================================================================================
  // 🚨 VBA 원본 pwfuc! 함수: Supply Fan 동력 계산
  const calculateSupplyFanPower = useCallback((vol: number, sp: number, ef: number, sf: number) => {
    try {
      // VBA 공식: pw = vol * Sp / (3600000 * ef / 100) * (1 + sf / 100)
      const pw = vol * sp / (3600000 * ef / 100) * (1 + sf / 100);
      
      // VBA 조건: pw < 0.01 또는 pw >= 220인 경우 0 반환
      if (pw < 0.01 || pw >= 220) {
        return 0;
      }
      
      // VBA pwfuc! 로직: 계산된 pw보다 크거나 같은 첫 번째 값 반환
      // 정확히 일치하는 값이 있는지 먼저 확인
      const exactMatchIndex = rtsPwfucLookupData.findIndex(value => Math.abs(value - pw) < 0.001);
      
      if (exactMatchIndex !== -1) {
        // 정확히 일치하는 값이 있으면 해당 값 반환
        return Math.round(rtsPwfucLookupData[exactMatchIndex] * 100) / 100;
      } else {
        // pw보다 크거나 같은 첫 번째 값 찾기
        const higherOrEqualIndex = rtsPwfucLookupData.findIndex(value => value >= pw);
        
        if (higherOrEqualIndex !== -1) {
          return Math.round(rtsPwfucLookupData[higherOrEqualIndex] * 100) / 100;
        } else {
          // pw가 모든 값보다 크면 마지막 값 반환
          return Math.round(rtsPwfucLookupData[rtsPwfucLookupData.length - 1] * 100) / 100;
        }
      }
    } catch (error) {
      console.error('Supply Fan Power calculation error:', error);
      return 0;
    }
  }, []);

  // ========================================================================================
  // Return Fan 동력 계산 함수 - Supply Fan과 동일한 로직
  // ========================================================================================
  const calculateReturnFanPower = useCallback((vol: number, sp: number, ef: number, sf: number) => {
    // Return Fan도 동일한 pwfuc! 함수 사용
    return calculateSupplyFanPower(vol, sp, ef, sf);
  }, [calculateSupplyFanPower]);

  // ========================================================================================
  // Return Fan 정압 계산 함수 - VBA ahucode1.bas fan_cal 함수와 100% 동일한 로직
  // ========================================================================================
  // 🚨 VBA 원본 lines 2319-2328: Return Fan 정압 계산
  const calculateReturnFanStaticPressure = useCallback((data: FanSelectionData) => {
    // Return Fan이 "None"인 경우 정압 = 0
    if (data.criteria.raFanPosition === 'None') {
      return 0;
    }
    
    // Return Fan이 "Outside" 또는 "Inside"인 경우
    // SPr1: External S.P RA 값들의 합계 (VBA line 2325)
    const returnTotal = (data.externalPressureDrop.return.ductFriction1 * data.externalPressureDrop.return.ductLength1) +
                        (data.externalPressureDrop.return.ductFriction2 * data.externalPressureDrop.return.ductLength2);
    
    const SPr1 = returnTotal + 
                 Object.values(data.externalPressureDrop.equipment).reduce(
                   (sum, equipment) => sum + (equipment?.ra || 0), 
                   data.externalPressureDrop.ductFittings.raValue || 0
                 );
    
    // 안전율 (External용)
    const Sf2 = data.criteria.safetyPercentage1 || 10; // Ext. 안전율
    
    // SPr 공식 (VBA line 2327): SPr1 + Round(SPr1 * Sf2 / 100, 1)
    const SPr = SPr1 + Math.round((SPr1 * Sf2 / 100) * 10) / 10;
    
    // Return Fan 정압 = Round Up(SPr, 0) (VBA line 2328)
    return Math.ceil(SPr);
  }, []);

  // ========================================================================================
  // SF S.P 합계 계산 함수 - VBA ahucode1.bas fan_cal 함수와 100% 동일한 로직
  // ========================================================================================
  // 🚨 중요: 이 함수는 VBA 원본(ahucode1.bas lines 2300-2318)과 정확히 일치하도록 검증됨
  // 🚨 수정 금지: 계산 로직을 변경하지 말 것 - VBA 원본과의 일치성이 검증된 상태
  // ========================================================================================
  const calculateSfStaticPressure = useCallback((data: FanSelectionData) => {
    // SPs1: Internal S.P 항목들의 합계
    // VBA 원본: ahdata.Cells(5, 66+R) 값들의 합계 (lines 2303, 2310)
    // 🚨 중요: 계산된 필드(total, safetyFactorPercent, safetyFactorValue, subTotal)는 제외
    const SPs1 =
      data.internalPressureDrop.coolingCoil +
      data.internalPressureDrop.reheatingCoil +
      data.internalPressureDrop.preheatingCoil +
      data.internalPressureDrop.heatingCoil +
      data.internalPressureDrop.heatRecovery +
      data.internalPressureDrop.preFilter +
      data.internalPressureDrop.mediumFilter +
      data.internalPressureDrop.finalFilter +
      data.internalPressureDrop.plenumCasing +
      data.internalPressureDrop.chamber +
      data.internalPressureDrop.volumeDamper +
      data.internalPressureDrop.spare1 +
      data.internalPressureDrop.spare2 +
      data.internalPressureDrop.spare3;
    
    // SPs2: External S.P SA 값들의 합계
    // VBA 원본: ahdata.Cells(5, 80+R) 값들의 합계 (lines 2304, 2311)
    // Pa/m × m 계산값들 + Equipment SA 값들 + Duct Fittings SA 값
    const supplyTotal = (data.externalPressureDrop.supply.ductFriction1 * data.externalPressureDrop.supply.ductLength1) +
                       (data.externalPressureDrop.supply.ductFriction2 * data.externalPressureDrop.supply.ductLength2);
    const SPs2 = supplyTotal + 
                 Object.values(data.externalPressureDrop.equipment).reduce(
                   (sum, equipment) => sum + (equipment?.sa || 0), 
                   data.externalPressureDrop.ductFittings.saValue || 0
                 );
    
    // SPs3: External S.P RA 값들의 합계 - Return Fan 모드에 따라 달라짐
    // VBA 원본: ahdata.Cells(6, 80+R) 값들의 합계 (line 2305)
    // 🚨 핵심 로직: Return Fan 설정에 따른 조건부 계산
    // - Return Fan = "None" → SPs3에 RA 값들 포함 (line 2305)
    // - Return Fan = "Outside" 또는 "Inside" → SPs3 = 0 (line 2313)
    const returnTotal = (data.externalPressureDrop.return.ductFriction1 * data.externalPressureDrop.return.ductLength1) +
                        (data.externalPressureDrop.return.ductFriction2 * data.externalPressureDrop.return.ductLength2);
    
    let SPs3 = 0;  // VBA 원본 line 2313: SPs3 = 0 (Outside/Inside 모드에서)
    if (data.criteria.raFanPosition === 'None') {
      // Return Fan이 None일 때만 RA 값들을 SPs3에 포함 (VBA 원본 line 2305)
      SPs3 = returnTotal + 
             Object.values(data.externalPressureDrop.equipment).reduce(
               (sum, equipment) => sum + (equipment?.ra || 0), 
               data.externalPressureDrop.ductFittings.raValue || 0
             );
    }
    
    // Sf1, Sf2: 안전율 (Safety Factor)
    // VBA에서 Sf1은 Internal용, Sf2는 External용
    const Sf1 = data.criteria.safetyPercentage2 || 10; // Int. 안전율
    const Sf2 = data.criteria.safetyPercentage1 || 10; // Ext. 안전율
    
    
    // ========================================================================================
    // 🚨 VBA 원본 공식 100% 정확 복제 - 수정 절대 금지
    // ========================================================================================
    // VBA ahucode1.bas lines 2317-2318 완전 동일 복제
    // Label545 (.Label545.Caption): SF S.P Internal 계산
    // Label546 (.Label546.Caption): SF S.P External 계산
    // ========================================================================================
    
    // Internal 공식 (VBA line 2317): SPs1 + Round(SPs1 * Sf1 / 100, 1)
    // 🚨 Math.round 순서와 소수점 처리 방식 변경 금지 - VBA와 정확히 일치함
    const internal = Math.round((SPs1 + Math.round((SPs1 * Sf1 / 100) * 10) / 10) * 10) / 10;
    
    // External 공식 (VBA line 2318): SPs2 + SPs3 + Round(SPs2 * Sf2 / 100, 1) + Round(SPs3 * Sf2 / 100, 1)
    // 🚨 Math.round 순서와 소수점 처리 방식 변경 금지 - VBA와 정확히 일치함
    const external = Math.round((
      SPs2 + SPs3 + 
      Math.round((SPs2 * Sf2 / 100) * 10) / 10 + 
      Math.round((SPs3 * Sf2 / 100) * 10) / 10
    ) * 10) / 10;
    
    // Total 계산: Internal + External
    const total = Math.round((internal + external) * 10) / 10;
    
    
    return { internal, external, total };
  }, []);

  // localStorage 저장 함수 - dispatch 제거 (별도 useEffect에서 처리)
  const saveToLocalStorage = React.useCallback((data: FanSelectionData) => {
    try {
      // localStorage에만 저장 (GlobalState는 별도 useEffect에서 처리)
      if (state.currentAHUId) {
        const ahuSpecificKey = `fan-selection-form-data-${state.currentAHUId}`;
        localStorage.setItem(ahuSpecificKey, JSON.stringify(data));
        //console.log(`✅ [FAN] localStorage 저장 완료: ${ahuSpecificKey}`);

        // 재열 코일 값 추적
        if (data.internalPressureDrop.reheatingCoil !== undefined) {
          console.log(`💾 [FAN] reheatingCoil 저장:`, {
            ahuId: state.currentAHUId,
            reheatingCoil: data.internalPressureDrop.reheatingCoil
          });
        }
      }
      localStorage.setItem('fan-selection-form-data', JSON.stringify(data));

      // AirflowSelectionTab에 RA Fan Position 변경 알림 (습공기 선도 RF 심볼 업데이트용)
      window.dispatchEvent(new Event('fan-selection-updated'));
    } catch (error) {
      console.error('Failed to save fan selection data to localStorage:', error);
    }
  }, [state.currentAHUId]);

  // ⚠️ 마운트 시 저장 제거: 로드된 데이터를 덮어쓰는 문제 방지
  // saveToLocalStorage는 updateInternalPressure 등 실제 변경 시에만 호출됨

  // fanData 변경 시 GlobalState에 저장 (렌더링 밖에서 실행)
  React.useEffect(() => {
    if (state.currentAHUId && currentAHUData) {
      const updatedAHU = {
        ...currentAHUData,
        fanSelectionData: fanData,
        updatedAt: new Date().toISOString()
      };
      dispatch({ type: 'UPDATE_AHU', payload: updatedAHU });
      //console.log(`✅ [FAN] GlobalState 저장 완료 (AHU ID: ${state.currentAHUId})`);
    }
  }, [fanData, state.currentAHUId, currentAHUData, dispatch]);

  // 코일 타입이 None으로 변경될 때 해당 압력손실 값을 0으로 설정
  React.useEffect(() => {
    if (!coilTypes) return;
    
    setFanData(prev => {
      let needsUpdate = false;
      const newInternalPressureDrop = { ...prev.internalPressureDrop };
      
      // 냉각 코일이 None인 경우
      if (coilTypes.coolingCoilType === 'None' && newInternalPressureDrop.coolingCoil !== 0) {
        newInternalPressureDrop.coolingCoil = 0;
        needsUpdate = true;
      }
      
      // 재열 코일이 None인 경우
      if (coilTypes.reheatingCoilType === 'None' && newInternalPressureDrop.reheatingCoil !== 0) {
        newInternalPressureDrop.reheatingCoil = 0;
        needsUpdate = true;
      }
      
      // 예열 코일이 None인 경우
      if (coilTypes.preheatingCoilType === 'None' && newInternalPressureDrop.preheatingCoil !== 0) {
        newInternalPressureDrop.preheatingCoil = 0;
        needsUpdate = true;
      }
      
      // 가열 코일이 None인 경우
      if (coilTypes.heatingCoilType === 'None' && newInternalPressureDrop.heatingCoil !== 0) {
        newInternalPressureDrop.heatingCoil = 0;
        needsUpdate = true;
      }
      
      if (needsUpdate) {
        // SF S.P 재계산
        const tempData = { ...prev, internalPressureDrop: newInternalPressureDrop };
        const sfStaticPressure = calculateSfStaticPressure(tempData);
        
        // 🚨 Supply Fan과 Return Fan 정압도 자동 업데이트
        const returnFanStaticPressure = calculateReturnFanStaticPressure(tempData);
        
        const newData = {
          ...prev,
          internalPressureDrop: newInternalPressureDrop,
          sfStaticPressure,
          supplyFan: {
            ...prev.supplyFan,
            staticPressure: sfStaticPressure.total
          },
          returnFan: {
            ...prev.returnFan,
            staticPressure: returnFanStaticPressure
          }
        };
        
        // localStorage에 저장
        saveToLocalStorage(newData);
        return newData;
      }
      
      return prev;
    });
  }, [coilTypes, calculateSfStaticPressure, calculateReturnFanStaticPressure, saveToLocalStorage]);

  // 초기 로딩 시 및 풍량 변경 시 SF S.P 계산 및 Fan 정압/동력 동기화 실행
  React.useEffect(() => {
    const sfStaticPressure = calculateSfStaticPressure(fanData);
    const returnFanStaticPressure = calculateReturnFanStaticPressure(fanData);

    // 동력 계산
    const supplyPower = calculateSupplyFanPower(
      fanData.supplyFan.airflow,
      Math.ceil(sfStaticPressure.total),
      fanData.supplyFan.efficiency,
      fanData.supplyFan.safetyFactor
    );

    const returnPower = calculateReturnFanPower(
      fanData.returnFan.airflow,
      Math.ceil(returnFanStaticPressure),
      fanData.returnFan.efficiency,
      fanData.returnFan.safetyFactor
    );

    if (sfStaticPressure.internal !== fanData.sfStaticPressure.internal ||
        sfStaticPressure.external !== fanData.sfStaticPressure.external ||
        sfStaticPressure.total !== fanData.sfStaticPressure.total ||
        fanData.supplyFan.staticPressure !== sfStaticPressure.total ||
        fanData.returnFan.staticPressure !== returnFanStaticPressure ||
        fanData.supplyFan.power !== supplyPower ||
        fanData.returnFan.power !== returnPower) {
      setFanData(prev => ({
        ...prev,
        sfStaticPressure,
        // 🚨 Supply Fan과 Return Fan 정압과 동력 모두 계산된 값으로 동기화
        supplyFan: {
          ...prev.supplyFan,
          staticPressure: sfStaticPressure.total,
          power: supplyPower
        },
        returnFan: {
          ...prev.returnFan,
          staticPressure: returnFanStaticPressure,
          power: returnPower
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fanData.supplyFan.airflow, fanData.returnFan.airflow]); // 풍량 변경 시에도 실행

  // ⚠️⚠️⚠️ 절대 활성화하지 마세요 - 이 useEffect는 영구적으로 비활성화됨 ⚠️⚠️⚠️
  //
  // 🚨 문제: ahuCalculationResults 기반 업데이트는 다음과 같은 버그를 발생시킴:
  //    1. Props로 최신 계산값 (예: 29100) 전달됨
  //    2. Line 1490 useEffect가 fanData를 29100으로 업데이트
  //    3. fanData 변경으로 인해 이 useEffect가 트리거됨 (dependency에 fanData 포함)
  //    4. ahuCalculationResults의 오래된 값 (예: 24900)으로 다시 덮어씀
  //    5. 무한 루프 발생 및 잘못된 값 표시
  //
  // ✅ 해결책: Props (calculatedSupplyAirflow/calculatedReturnAirflow)가 항상 최신값임
  //           Line 1490의 useEffect에서 props 기반 업데이트로 충분함
  //           ahuCalculationResults는 fallback 용도로만 사용 (line 1382-1383)
  //
  // 📅 비활성화 날짜: 2025-01-XX
  // 🐛 관련 버그: FAN 선정 탭에서 계산된 풍량이 반영되지 않는 문제
  //
  // React.useEffect(() => {
  //   if (ahuCalculationResults?.supplyFan && ahuCalculationResults?.returnFan) {
  //     const { supplyFan, returnFan } = ahuCalculationResults;
  //     ... (50+ lines of code)
  //     setFanData(prev => ({ ...prev, supplyFan: {...}, returnFan: {...} }));
  //   }
  // }, [ahuCalculationResults, fanData, ...]); // ❌ fanData dependency가 무한 루프 발생
  //
  // ⚠️⚠️⚠️ 이 주석을 삭제하지 마세요 - 미래의 개발자를 위한 경고입니다 ⚠️⚠️⚠️

  // 상태 업데이트 헬퍼 함수들 - useCallback으로 함수 참조 안정화
  const updateCriteria = useCallback((field: keyof FanSelectionData['criteria'], value: any) => {
    setFanData(prev => {
      const newCriteria = { ...prev.criteria, [field]: value };
      
      // 안전율이 변경되거나 RA Fan 위치가 변경되면 SF S.P 재계산
      if (field === 'safetyPercentage1' || field === 'safetyPercentage2' || field === 'raFanPosition') {
        const tempData = { ...prev, criteria: newCriteria };
        const sfStaticPressure = calculateSfStaticPressure(tempData);
        
        // 🚨 Supply Fan과 Return Fan 정압도 자동 업데이트
        const returnFanStaticPressure = calculateReturnFanStaticPressure(tempData);
        
        const newData = {
          ...prev,
          criteria: newCriteria,
          sfStaticPressure,
          supplyFan: {
            ...prev.supplyFan,
            staticPressure: sfStaticPressure.total
          },
          returnFan: {
            ...prev.returnFan,
            staticPressure: returnFanStaticPressure
          }
        };
        saveToLocalStorage(newData);
        return newData;
      }
      
      const newData = {
        ...prev,
        criteria: newCriteria
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSfStaticPressure, calculateReturnFanStaticPressure]);

  const updateSupplyFan = useCallback((field: keyof FanSelectionData['supplyFan'], value: any) => {
    setFanData(prev => {
      const newSupplyFan = { ...prev.supplyFan, [field]: value };
      
      // 효율 또는 안전율이 변경된 경우 동력 재계산
      let updatedSupplyFan = newSupplyFan;
      if (field === 'efficiency' || field === 'safetyFactor') {
        const supplyPower = calculateSupplyFanPower(
          newSupplyFan.airflow,
          Math.ceil(newSupplyFan.staticPressure),
          newSupplyFan.efficiency,
          newSupplyFan.safetyFactor
        );
        updatedSupplyFan = { ...newSupplyFan, power: supplyPower };
      }
      
      const newData = {
        ...prev,
        supplyFan: updatedSupplyFan
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSupplyFanPower]);

  const updateReturnFan = useCallback((field: keyof FanSelectionData['returnFan'], value: any) => {
    setFanData(prev => {
      const newReturnFan = { ...prev.returnFan, [field]: value };
      
      // 효율 또는 안전율이 변경된 경우 동력 재계산
      let updatedReturnFan = newReturnFan;
      if (field === 'efficiency' || field === 'safetyFactor') {
        const returnPower = calculateReturnFanPower(
          newReturnFan.airflow,
          Math.ceil(newReturnFan.staticPressure),
          newReturnFan.efficiency,
          newReturnFan.safetyFactor
        );
        updatedReturnFan = { ...newReturnFan, power: returnPower };
      }
      
      const newData = {
        ...prev,
        returnFan: updatedReturnFan
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateReturnFanPower]);

  const updateInternalPressure = useCallback((field: keyof FanSelectionData['internalPressureDrop'], value: number) => {
    setFanData(prev => {
      const newInternalPressureDrop = { ...prev.internalPressureDrop, [field]: value };
      
      // SF S.P 재계산
      const tempData = { ...prev, internalPressureDrop: newInternalPressureDrop };
      const sfStaticPressure = calculateSfStaticPressure(tempData);
      
      // 🚨 Supply Fan과 Return Fan 정압 및 동력 자동 업데이트
      const tempDataForReturn = { ...prev, internalPressureDrop: newInternalPressureDrop };
      const returnFanStaticPressure = calculateReturnFanStaticPressure(tempDataForReturn);
      
      // 동력 계산: AHU Selection에서 온 풍량과 현재 효율/안전율 사용
      const supplyPower = calculateSupplyFanPower(
        prev.supplyFan.airflow, 
        Math.ceil(sfStaticPressure.total), // VBA처럼 Round Up 적용
        prev.supplyFan.efficiency, 
        prev.supplyFan.safetyFactor
      );
      
      const returnPower = calculateReturnFanPower(
        prev.returnFan.airflow,
        Math.ceil(returnFanStaticPressure), // VBA처럼 Round Up 적용
        prev.returnFan.efficiency,
        prev.returnFan.safetyFactor
      );
      
      const newData = {
        ...prev,
        internalPressureDrop: newInternalPressureDrop,
        sfStaticPressure,
        supplyFan: {
          ...prev.supplyFan,
          staticPressure: sfStaticPressure.total,
          power: supplyPower
        },
        returnFan: {
          ...prev.returnFan,
          staticPressure: returnFanStaticPressure,
          power: returnPower
        }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSfStaticPressure, calculateReturnFanStaticPressure, calculateSupplyFanPower, calculateReturnFanPower]);

  const updateSfStaticPressure = useCallback((field: keyof FanSelectionData['sfStaticPressure'], value: number) => {
    setFanData(prev => {
      const newSfStaticPressure = { ...prev.sfStaticPressure, [field]: value };
      // VBA 코드와 동일하게 Internal + External = Total 계산
      if (field === 'internal' || field === 'external') {
        newSfStaticPressure.total = newSfStaticPressure.internal + newSfStaticPressure.external;
      }
      const newData = {
        ...prev,
        sfStaticPressure: newSfStaticPressure
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSfStaticPressure]);

  const updateExternalSupply = useCallback((field: keyof FanSelectionData['externalPressureDrop']['supply'], value: number) => {
    setFanData(prev => {
      const newExternalPressureDrop = {
        ...prev.externalPressureDrop,
        supply: { ...prev.externalPressureDrop.supply, [field]: value }
      };
      
      // SF S.P 재계산
      const tempData = { ...prev, externalPressureDrop: newExternalPressureDrop };
      const sfStaticPressure = calculateSfStaticPressure(tempData);
      
      // 🚨 Supply Fan과 Return Fan 정압도 자동 업데이트
      const returnFanStaticPressure = calculateReturnFanStaticPressure(tempData);
      
      const newData = {
        ...prev,
        externalPressureDrop: newExternalPressureDrop,
        sfStaticPressure,
        supplyFan: {
          ...prev.supplyFan,
          staticPressure: sfStaticPressure.total
        },
        returnFan: {
          ...prev.returnFan,
          staticPressure: returnFanStaticPressure
        }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSfStaticPressure, calculateReturnFanStaticPressure]);

  const updateExternalReturn = useCallback((field: keyof FanSelectionData['externalPressureDrop']['return'], value: number) => {
    setFanData(prev => {
      const newExternalPressureDrop = {
        ...prev.externalPressureDrop,
        return: { ...prev.externalPressureDrop.return, [field]: value }
      };
      
      // SF S.P 재계산
      const tempData = { ...prev, externalPressureDrop: newExternalPressureDrop };
      const sfStaticPressure = calculateSfStaticPressure(tempData);
      
      // 🚨 Supply Fan과 Return Fan 정압도 자동 업데이트
      const returnFanStaticPressure = calculateReturnFanStaticPressure(tempData);
      
      const newData = {
        ...prev,
        externalPressureDrop: newExternalPressureDrop,
        sfStaticPressure,
        supplyFan: {
          ...prev.supplyFan,
          staticPressure: sfStaticPressure.total
        },
        returnFan: {
          ...prev.returnFan,
          staticPressure: returnFanStaticPressure
        }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSfStaticPressure, calculateReturnFanStaticPressure]);

  const updateDuctFittings = useCallback((field: keyof FanSelectionData['externalPressureDrop']['ductFittings'], value: number) => {
    setFanData(prev => {
      const newExternalPressureDrop = {
        ...prev.externalPressureDrop,
        ductFittings: { ...prev.externalPressureDrop.ductFittings, [field]: value }
      };
      
      // SF S.P 재계산
      const tempData = { ...prev, externalPressureDrop: newExternalPressureDrop };
      const sfStaticPressure = calculateSfStaticPressure(tempData);
      
      // 🚨 Supply Fan과 Return Fan 정압도 자동 업데이트
      const returnFanStaticPressure = calculateReturnFanStaticPressure(tempData);
      
      const newData = {
        ...prev,
        externalPressureDrop: newExternalPressureDrop,
        sfStaticPressure,
        supplyFan: {
          ...prev.supplyFan,
          staticPressure: sfStaticPressure.total
        },
        returnFan: {
          ...prev.returnFan,
          staticPressure: returnFanStaticPressure
        }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSfStaticPressure, calculateReturnFanStaticPressure]);

  const updateEquipment = useCallback((equipmentKey: keyof FanSelectionData['externalPressureDrop']['equipment'], field: 'sa' | 'ra', value: number) => {
    setFanData(prev => {
      const newExternalPressureDrop = {
        ...prev.externalPressureDrop,
        equipment: {
          ...prev.externalPressureDrop.equipment,
          [equipmentKey]: { 
            ...prev.externalPressureDrop.equipment?.[equipmentKey] || { sa: 0, ra: 0 }, 
            [field]: value 
          }
        }
      };
      
      // SF S.P 재계산
      const tempData = { ...prev, externalPressureDrop: newExternalPressureDrop };
      const sfStaticPressure = calculateSfStaticPressure(tempData);
      
      // 🚨 Supply Fan과 Return Fan 정압도 자동 업데이트
      const returnFanStaticPressure = calculateReturnFanStaticPressure(tempData);
      
      const newData = {
        ...prev,
        externalPressureDrop: newExternalPressureDrop,
        sfStaticPressure,
        supplyFan: {
          ...prev.supplyFan,
          staticPressure: sfStaticPressure.total
        },
        returnFan: {
          ...prev.returnFan,
          staticPressure: returnFanStaticPressure
        }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage, calculateSfStaticPressure, calculateReturnFanStaticPressure]);



  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      p: 2,
      // 숫자 입력 필드에서 스핀 버튼 숨기기
      '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '& input[type=number]': {
        MozAppearance: 'textfield',
      },
    }}>
      <Grid container spacing={2}>
        {/* 왼쪽 섹션 - 3/6 비율 (50%) */}
        <Grid item xs={12} md={6}>
          <FanCriteriaSection data={fanData.criteria} onUpdate={updateCriteria} />
          <SupplyFanSection data={fanData.supplyFan} onUpdate={updateSupplyFan} />
          {fanData.criteria.raFanPosition !== 'None' && (
            <ReturnFanSection data={fanData.returnFan} onUpdate={updateReturnFan} />
          )}
        </Grid>

        {/* 중앙 섹션 - 1/6 비율 (16.7%) */}
        <Grid item xs={12} md={2}>
          <InternalPressureSection
            data={fanData.internalPressureDrop}
            onUpdate={updateInternalPressure}
            coilTypes={coilTypes}
            heatRecoveryType={heatRecoveryType}
          />
          <SfStaticPressureSection data={fanData.sfStaticPressure} />
        </Grid>

        {/* 오른쪽 섹션 - 2/6 비율 (33.3%) */}
        <Grid item xs={12} md={4}>
          <ExternalPressureSection
            data={fanData.externalPressureDrop}
            onUpdateSupply={updateExternalSupply}
            onUpdateReturn={updateExternalReturn}
            onUpdateDuctFittings={updateDuctFittings}
            onUpdateEquipment={updateEquipment}
            raFanPosition={fanData.criteria.raFanPosition}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default FanSelectionForm;
